import React, { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api from "@/lib/api";
import { 
  TrendingUp, 
  TrendingDown,
  IndianRupee,
  Package,
  Star,
  Truck,
  Plane,
  Bike,
  Car,
  RefreshCw,
  Loader2,
  CheckCircle,
  XCircle,
  Clock,
  MapPin,
  BarChart3
} from "lucide-react";

export default function PartnerAnalytics() {
  const [period, setPeriod] = useState("30d");
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [topRoutes, setTopRoutes] = useState([]);
  const [performance, setPerformance] = useState(null);

  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    try {
      const [overviewRes, chartRes, routesRes, perfRes] = await Promise.all([
        api.get(`/partner/analytics/overview?period=${period}`),
        api.get(`/partner/analytics/revenue-chart?period=${period}`),
        api.get("/partner/analytics/top-routes"),
        api.get("/partner/analytics/performance")
      ]);
      
      setOverview(overviewRes.data);
      setChartData(chartRes.data.chart_data || []);
      setTopRoutes(routesRes.data.routes || []);
      setPerformance(perfRes.data);
    } catch (error) {
      console.error("Failed to fetch analytics:", error);
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  // Calculate chart max for scaling
  const maxRevenue = Math.max(...chartData.map(d => d.revenue), 100);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="partner-analytics">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-black tracking-tight">Analytics</h1>
          <p className="text-muted-foreground">Track your business performance</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-32 rounded-none" data-testid="period-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7d">Last 7 days</SelectItem>
              <SelectItem value="30d">Last 30 days</SelectItem>
              <SelectItem value="90d">Last 90 days</SelectItem>
            </SelectContent>
          </Select>
          <Button 
            variant="outline" 
            size="icon" 
            onClick={fetchAnalytics}
            className="rounded-none"
            data-testid="refresh-analytics-btn"
          >
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-border border border-border">
        {/* Total Revenue */}
        <div className="bg-background p-6" data-testid="revenue-card">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm text-muted-foreground">Total Revenue</span>
            <div className="w-10 h-10 bg-emerald-500 flex items-center justify-center">
              <IndianRupee className="w-5 h-5 text-white" />
            </div>
          </div>
          <p className="text-3xl font-black">{formatCurrency(overview?.summary?.total_revenue || 0)}</p>
          <div className={`flex items-center gap-1 text-sm mt-2 ${
            (overview?.summary?.revenue_change || 0) >= 0 ? 'text-emerald-600' : 'text-red-600'
          }`}>
            {(overview?.summary?.revenue_change || 0) >= 0 
              ? <TrendingUp className="w-4 h-4" />
              : <TrendingDown className="w-4 h-4" />
            }
            <span>{Math.abs(overview?.summary?.revenue_change || 0)}% vs previous period</span>
          </div>
        </div>

        {/* Total Orders */}
        <div className="bg-background p-6" data-testid="orders-card">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm text-muted-foreground">Total Orders</span>
            <div className="w-10 h-10 bg-blue-500 flex items-center justify-center">
              <Package className="w-5 h-5 text-white" />
            </div>
          </div>
          <p className="text-3xl font-black">{overview?.summary?.total_orders || 0}</p>
          <div className={`flex items-center gap-1 text-sm mt-2 ${
            (overview?.summary?.orders_change || 0) >= 0 ? 'text-emerald-600' : 'text-red-600'
          }`}>
            {(overview?.summary?.orders_change || 0) >= 0 
              ? <TrendingUp className="w-4 h-4" />
              : <TrendingDown className="w-4 h-4" />
            }
            <span>{Math.abs(overview?.summary?.orders_change || 0)}% vs previous period</span>
          </div>
        </div>

        {/* Completion Rate */}
        <div className="bg-background p-6" data-testid="completion-card">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm text-muted-foreground">Completion Rate</span>
            <div className="w-10 h-10 bg-orange-500 flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-white" />
            </div>
          </div>
          <p className="text-3xl font-black">{overview?.summary?.completion_rate || 0}%</p>
          <p className="text-sm text-muted-foreground mt-2">
            {overview?.breakdown?.completed_deliveries || 0} completed / {overview?.breakdown?.total_deliveries || 0} total
          </p>
        </div>

        {/* Average Rating */}
        <div className="bg-background p-6" data-testid="rating-card">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm text-muted-foreground">Average Rating</span>
            <div className="w-10 h-10 bg-amber-500 flex items-center justify-center">
              <Star className="w-5 h-5 text-white" />
            </div>
          </div>
          <p className="text-3xl font-black">{overview?.summary?.avg_rating || 0}</p>
          <p className="text-sm text-muted-foreground mt-2">
            Based on {overview?.summary?.total_reviews || 0} reviews
          </p>
        </div>
      </div>

      {/* Revenue Chart */}
      <div className="border border-border p-6">
        <h2 className="text-lg font-bold mb-6 flex items-center gap-2">
          <BarChart3 className="w-5 h-5" />
          Revenue Trend
        </h2>
        
        {chartData.length > 0 ? (
          <div className="space-y-4">
            {/* Chart bars */}
            <div className="flex items-end gap-1 h-48">
              {chartData.map((day, index) => {
                const heightPercent = maxRevenue > 0 ? (day.revenue / maxRevenue) * 100 : 0;
                return (
                  <div key={index} className="flex-1 flex flex-col items-center group relative">
                    <div 
                      className="w-full bg-primary/80 hover:bg-primary transition-colors rounded-t cursor-pointer"
                      style={{ height: `${Math.max(heightPercent, 2)}%` }}
                    />
                    {/* Tooltip */}
                    <div className="absolute bottom-full mb-2 hidden group-hover:block bg-foreground text-background text-xs px-2 py-1 rounded whitespace-nowrap z-10">
                      {formatCurrency(day.revenue)}
                      <br />
                      {day.bookings + day.deliveries} orders
                    </div>
                  </div>
                );
              })}
            </div>
            
            {/* X-axis labels */}
            <div className="flex gap-1 text-xs text-muted-foreground overflow-hidden">
              {chartData.filter((_, i) => i % Math.ceil(chartData.length / 7) === 0).map((day, index) => (
                <div key={index} className="flex-1 text-center truncate">
                  {new Date(day.date).toLocaleDateString('en-IN', { month: 'short', day: 'numeric' })}
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-center h-48 text-muted-foreground">
            No data available for this period
          </div>
        )}
      </div>

      {/* Bottom Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Revenue Breakdown */}
        <div className="border border-border p-6">
          <h2 className="text-lg font-bold mb-4">Revenue Breakdown</h2>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-3 bg-secondary/50">
              <div className="flex items-center gap-3">
                <Truck className="w-5 h-5 text-blue-500" />
                <span>Cargo & Jet Bookings</span>
              </div>
              <span className="font-bold">{formatCurrency(overview?.breakdown?.booking_revenue || 0)}</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-secondary/50">
              <div className="flex items-center gap-3">
                <Bike className="w-5 h-5 text-orange-500" />
                <span>Local Deliveries</span>
              </div>
              <span className="font-bold">{formatCurrency(overview?.breakdown?.delivery_revenue || 0)}</span>
            </div>
            <div className="h-px bg-border" />
            <div className="flex items-center justify-between p-3 font-bold">
              <span>Total</span>
              <span className="text-primary">{formatCurrency(overview?.summary?.total_revenue || 0)}</span>
            </div>
          </div>
        </div>

        {/* Top Routes */}
        <div className="border border-border p-6">
          <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
            <MapPin className="w-5 h-5" />
            Top Routes
          </h2>
          {topRoutes.length > 0 ? (
            <div className="space-y-3">
              {topRoutes.map((route, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-secondary/50">
                  <div className="flex items-center gap-3">
                    <span className="w-6 h-6 bg-primary text-primary-foreground flex items-center justify-center text-xs font-bold">
                      {index + 1}
                    </span>
                    <span className="text-sm">{route.route}</span>
                  </div>
                  <span className="text-sm text-muted-foreground">{route.count} deliveries</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-muted-foreground text-center py-8">No delivery data yet</p>
          )}
        </div>
      </div>

      {/* Performance Metrics */}
      <div className="border border-border p-6">
        <h2 className="text-lg font-bold mb-4">Performance Metrics</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Delivery Status */}
          <div>
            <h3 className="text-sm font-medium text-muted-foreground mb-3">Delivery Status</h3>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-emerald-500" />
                  <span className="text-sm">Delivered</span>
                </div>
                <span className="font-medium">{performance?.delivery_status?.delivered || 0}</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-amber-500" />
                  <span className="text-sm">In Transit</span>
                </div>
                <span className="font-medium">{performance?.delivery_status?.in_transit || 0}</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <XCircle className="w-4 h-4 text-red-500" />
                  <span className="text-sm">Cancelled</span>
                </div>
                <span className="font-medium">{performance?.delivery_status?.cancelled || 0}</span>
              </div>
            </div>
          </div>

          {/* Vehicle Breakdown */}
          <div>
            <h3 className="text-sm font-medium text-muted-foreground mb-3">Vehicle Usage</h3>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Bike className="w-4 h-4 text-blue-500" />
                  <span className="text-sm">Bike</span>
                </div>
                <span className="font-medium">{performance?.vehicle_breakdown?.bike || 0}</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Car className="w-4 h-4 text-purple-500" />
                  <span className="text-sm">Car</span>
                </div>
                <span className="font-medium">{performance?.vehicle_breakdown?.car || 0}</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Truck className="w-4 h-4 text-orange-500" />
                  <span className="text-sm">Van</span>
                </div>
                <span className="font-medium">{performance?.vehicle_breakdown?.van || 0}</span>
              </div>
            </div>
          </div>

          {/* Booking Types */}
          <div>
            <h3 className="text-sm font-medium text-muted-foreground mb-3">Service Breakdown</h3>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Truck className="w-4 h-4 text-blue-500" />
                  <span className="text-sm">Cargo</span>
                </div>
                <span className="font-medium">{performance?.booking_types?.cargo || 0}</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Plane className="w-4 h-4 text-amber-500" />
                  <span className="text-sm">Jet</span>
                </div>
                <span className="font-medium">{performance?.booking_types?.jet || 0}</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Bike className="w-4 h-4 text-orange-500" />
                  <span className="text-sm">Local Delivery</span>
                </div>
                <span className="font-medium">{performance?.booking_types?.local_delivery || 0}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
